// src/pages/cartao/SolicitacaoSegundaViaCartao.tsx
import 'bootstrap/dist/css/bootstrap.css';
import { useState } from 'react';
import Spinner from '../../shared/spinner';
import ScrollToTopButton from '../../shared/scrollToTopButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import InputMask from 'react-input-mask';
import CustomAlert from '../../shared/customAlert';
import ProcessadoraCartaoService from '../../services/processadoraCartaoService';

// Interface para os dados enviados no Body
interface SegundaViaBody {
  numeroProposta: string;
  embossName: string;
  line4Emboss: string;
  deficiencyVisualDescription: string;
  reasonReissueCard: string;
  flagRate: string;
}

// Interface para os dados de saída (retorno 200)
interface SegundaViaOutput {
  returnCode: number;
  returnDescription: string;
  newCardNumbernumeroProposta: string;
  deficiencyVisual: string;
  flagRate: 'S' | 'N';
  memberSince: string;
  validThru: string;
}

// Interface para o retorno de erro (422, 500)
interface SegundaViaError {
  returnCode: string;
  returnDescription: string;
}

function SolicitacaoSegundaViaCartao() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  // Estados dos campos do Body
  const [numeroProposta, setNumeroProposta] = useState(''); 
  const [embossName, setEmbossName] = useState('');
  const [line4Emboss, setLine4Emboss] = useState('');
  const [deficiencyVisualDescription, setDeficiencyVisualDescription] = useState('0');
  const [reasonReissueCard, setReasonReissueCard] = useState('R');
  const [flagRate, setFlagRate] = useState('N');

  const [resultadoSucesso, setResultadoSucesso] = useState<SegundaViaOutput | null>(null);
  const [resultadoErro, setResultadoErro] = useState<SegundaViaError | null>(null);
  const [exibeResultado, setExibeResultado] = useState(false);

  const processadoraCartaoService = new ProcessadoraCartaoService();

  // Função para solicitar segunda via real
  const solicitarSegundaVia = async () => {

    const requestBody: SegundaViaBody = {
      numeroProposta: numeroProposta, 
      embossName,
      line4Emboss,
      deficiencyVisualDescription,
      reasonReissueCard,
      flagRate,
    };

    setLoading(true);
    setResultadoSucesso(null);
    setResultadoErro(null);

    try {
      const response = await processadoraCartaoService.segundaViaCartao(requestBody);

      // Sucesso 200
      const data: SegundaViaOutput = response.data;
      setResultadoSucesso(data);
      setAlert({ message: 'Segunda via solicitada com sucesso!', type: 'success' });
      setExibeResultado(true);

    } catch (err: any) {
      const errorData: SegundaViaError = err.response?.data || {
        returnCode: err.response?.status?.toString() || '500',
        returnDescription: 'Erro desconhecido ao solicitar segunda via.'
      };
      setResultadoErro(errorData);
      setAlert({ message: `Erro ${errorData.returnCode}: ${errorData.returnDescription}`, type: 'error' });
      setExibeResultado(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <>
            <div className="card" style={{ marginTop: '25px' }}>
              <div className="card-body">
                <div className="card-header">
                  <strong>Solicitação de Segunda Via de Cartão</strong>
                </div>

                <h6 style={{ marginTop: '15px' }}>Dados da Solicitação</h6>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Número Proposta <span style={{ color: 'red' }}>*</span></label> 
                      <InputMask
                        mask="9999"
                        type="text"
                        className="form-control form-control-sm brds"
                        style={{ color: 'Black', backgroundColor: 'White' }}
                        value={numeroProposta} 
                        onChange={(e) => setNumeroProposta(e.target.value.replace(/_/g, ""))}
                        placeholder="Ex: 1234"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Nome do Portador <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        maxLength={26}
                        className="form-control form-control-sm brds"
                        style={{ color: 'Black', backgroundColor: 'White' }}
                        value={embossName}
                        onChange={(e) => setEmbossName(e.target.value)}
                        placeholder="Ex: NOME EMBOSSADO"
                      />
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="form-group">
                      <label>Linha Adicional <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        maxLength={19}
                        className="form-control form-control-sm brds"
                        style={{ color: 'Black', backgroundColor: 'White' }}
                        value={line4Emboss}
                        onChange={(e) => setLine4Emboss(e.target.value)}
                        placeholder="Informações adicionais (Ex: Kit Completo)"
                      />
                    </div>
                  </div>
                </div>

                <div className="row" style={{ marginTop: '10px' }}>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Descrição Deficiência Visual <span style={{ color: 'red' }}>*</span></label>
                      <select
                        className="form-control form-control-sm brds"
                        value={deficiencyVisualDescription}
                        onChange={(e) => setDeficiencyVisualDescription(e.target.value)}
                      >
                        <option value="0">Kit Completo</option>
                        <option value="BRAILE">Braile</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Motivo da Reemissão <span style={{ color: 'red' }}>*</span></label>
                      <select
                        className="form-control form-control-sm brds"
                        value={reasonReissueCard}
                        onChange={(e) => setReasonReissueCard(e.target.value)}
                      >
                        <option value="R">Roubo/Furto/Extravio</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Cobrança de Tarifa </label>
                      <select
                        className="form-control form-control-sm brds"
                        value={flagRate}
                        onChange={(e) => setFlagRate(e.target.value)}
                      >
                        <option value="N">Não (sem tarifa)</option>
                        <option value="S">Sim (com tarifa)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row" style={{ marginTop: '20px' }}>
                  <div className="col-md-4">
                    <button className="input-group-btn" id="App-button-cor-principal"
                      style={{
                        width: '80%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                      }}
                      onClick={solicitarSegundaVia}>
                      <FontAwesomeIcon icon={faPaperPlane} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                      <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Solicitar Segunda Via</strong>
                    </button>
                  </div>
                </div>

                {/* --- Resultado Sucesso --- */}
                {exibeResultado && resultadoSucesso && (
                  <>
                    <hr style={{ marginTop: '20px', marginBottom: '20px' }} />
                    <h4><strong>Status da Solicitação (Sucesso - 200)</strong></h4>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-3">
                        <label>Novos 4 Dígitos:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.newCardNumbernumeroProposta} style={{ backgroundColor: 'LightGrey' }} />
                      </div>
                      <div className="col-md-3">
                        <label>Data Válida Desde:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.memberSince} style={{ backgroundColor: 'LightGrey' }} />
                      </div>
                      <div className="col-md-3">
                        <label>Data de Validade:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.validThru} style={{ backgroundColor: 'LightGrey' }} />
                      </div>
                      <div className="col-md-3">
                        <label>Tarifa Cobrada:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.flagRate === 'S' ? 'Sim' : 'Não'} style={{ backgroundColor: 'LightGrey' }} />
                      </div>
                    </div>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-12">
                        <label>Mensagem de Retorno:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoSucesso.returnDescription} style={{ backgroundColor: 'LightGrey' }} />
                      </div>
                    </div>
                  </>
                )}

                {/* --- Resultado Erro --- */}
                {exibeResultado && resultadoErro && (
                  <>
                    <hr style={{ marginTop: '20px', marginBottom: '20px' }} />
                    <h4><strong>Status da Solicitação (Erro - {resultadoErro.returnCode})</strong></h4>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-2">
                        <label>Código de Retorno:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoErro.returnCode} style={{ backgroundColor: '#f8d7da', color: '#721c24' }} />
                      </div>
                      <div className="col-md-10">
                        <label>Descrição:</label>
                        <input type="text" readOnly className="form-control form-control-sm brds" value={resultadoErro.returnDescription} style={{ backgroundColor: '#f8d7da', color: '#721c24' }} />
                      </div>
                    </div>
                  </>
                )}

              </div>
            </div>
          </>
        )}
      </div>
      <ScrollToTopButton />
    </>
  );
}

export default SolicitacaoSegundaViaCartao;